import React, { useEffect, useRef, useState } from "react";
import Button from "antd/lib/button";
import Row from "antd/lib/row";
import Col from "antd/lib/col";
import TextArea, { TextAreaRef } from "antd/lib/input/TextArea";

import "./DellQuoteImportForm.less";
import { Form } from "@ant-design/compatible";
import "@ant-design/compatible/assets/index.css";
import {
	QuoteProduct,
	DellQuoteImportRequest
} from "@/Contracts/ProposalBuilder/Block";
import BlocksActionCreators from "@/ProposalBuilder/store/actions/Blocks";
import { DefaultErrorMessage } from "@/Contracts";
import { ValidationError } from "../../../../../Utils/ServerError";
import Modal from "antd/lib/modal/Modal";
import ZAlert from "@atoms/ZAlert";
import { useProposalBuilderDispatch } from "@/ProposalBuilder/store";

interface DellQuoteImportFormProps {
	readonly onClose: () => void;
	readonly onSubmit: (data: QuoteProduct[]) => void;
	readonly visible: boolean;
}
export const DellQuoteImportForm: React.FC<
	DellQuoteImportFormProps
> = props => {
	const textRef = useRef<TextAreaRef>(null);
	const [dellQuote, setDellQuote] = useState<string>("");
	const [isLoading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState(null);
	const proposalBuilderDispatch = useProposalBuilderDispatch();

	const verifyContent = async () => {
		try {
			if (!dellQuote?.length) {
				throw new ValidationError("The text content is invalid");
			}
			setLoading(true);
			setError(null);

			const request: DellQuoteImportRequest = {
				textContent: dellQuote,
				isDryRun: true
			};
			const { preview } = await proposalBuilderDispatch(
				BlocksActionCreators.importFromDellQuote(request)
			);
			if (!preview?.length) {
				throw new ValidationError(
					`We couldn't find any valid products from the text content`
				);
			}
			props.onSubmit(preview);
		} catch (error) {
			console.error(error);
			setError(error.displayMessage || DefaultErrorMessage);
		} finally {
			setLoading(false);
		}
	};

	const handleTextareaChange = (
		event: React.ChangeEvent<HTMLTextAreaElement>
	) => {
		setDellQuote(event.target.value);
	};

	useEffect(() => {
		if (props.visible) {
			setDellQuote("");
			if (textRef.current) {
				textRef.current.focus();
			}
			setError(null);
		}
	}, [props.visible]);

	return (
		<Modal
			title="Import from Dell Sales Quote"
			visible={props.visible}
			footer={null}
			onCancel={props.onClose}
		>
			<div className="dell-quote-modal">
				<Row>
					<Col span={24}>
						<ZAlert
							message={
								<div>
									We currently support only Dell Sales Quotes.{" "}
									<a
										href="https://help.zomentum.com/support/solutions/articles/44002150639-importing-products-from-dell-equote"
										target="_blank"
										rel="noopener noreferrer nofollow"
									>
										Learn more
									</a>
								</div>
							}
							type="warning"
							showIcon
							className="mb-4"
						/>
						<Form.Item
							validateStatus={error ? "error" : undefined}
							help={error}
						>
							<TextArea
								rows={8}
								ref={textRef}
								placeholder="Copy and paste your entire Sales Quote here"
								value={dellQuote}
								onChange={handleTextareaChange}
							/>
						</Form.Item>
					</Col>
				</Row>
				<div className="modal-footer">
					{!isLoading && (
						<Button
							type="default"
							htmlType="button"
							onClick={props.onClose}
							className="margin-right-8px"
						>
							Cancel
						</Button>
					)}
					<Button
						type="primary"
						htmlType="button"
						loading={isLoading}
						onClick={verifyContent}
					>
						Import
					</Button>
				</div>
			</div>
		</Modal>
	);
};
